import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, concatMap, delay, filter, map, switchMap } from 'rxjs/operators';
import { routeParams } from 'src/app/app.routes';
import { ErrorService } from 'src/app/services/error.service';
import { Attachment, AttachmentTypeName, Client, ClientTypeName, ClinicalNote, ClinicalNoteEmail, ClinicalNoteEmailTypeName, ClinicalNoteTypeName, CommunicationRecord, CommunicationRecordTypeName, DocumentInstance, DocumentInstanceTypeName, GraphAggregateChangeOperation, Patient, PatientRecordTask, PatientTypeName, TaskTypeName, Visit, VisitTypeName } from 'src/app/types/aggregate-graph.types';
import { connectHubCompleted } from '../aggregate-graph-hub/aggregate-graph-hub.actions';
import { accessToken } from '../auth/auth.selectors';
import { clientErrored } from '../error/error.actions';
import { AppState } from '../reducers';
import { selectRouteParam } from '../router/router.selectors';
import * as sheetGraphActions from '../sheet-graph/sheet-graph.actions';
import { sheetId } from '../sheet-graph/sheet-graph.selectors';
import * as sheetActions from '../sheet/sheet.actions';
import * as patientGraphActions from './patient-graph.actions';
import { entityIdentifiers, patientId } from './patient-graph.selectors';

const TransitionDelayInMilliseconds = 500;

@Injectable()
export class PatientGraphEffects {

	constructor(private actions$: Actions, private store: Store<AppState>, private errorService: ErrorService) {
	}

	patientIdParamChanged$ = createEffect(() =>
		this.store.select(selectRouteParam(routeParams.patientId)).pipe(
			concatLatestFrom(() => [
				this.store.select(accessToken),
				this.store.select(patientId),
				this.store.select(selectRouteParam(routeParams.taskId)),
				this.store.select(selectRouteParam(routeParams.serviceId))
			]),
			filter(([, accessToken]) => !!accessToken),
			switchMap(([patientId, , previousPatientId, taskId, serviceId]) => {
				const actions: any[] = [];
				if (previousPatientId) {
					actions.push(patientGraphActions.unsubscribePatientRequested());
				}
				if (patientId) {
					actions.push(patientGraphActions.subscribePatientRequested({ patientChanged: true, patientId: patientId, taskId: taskId ?? '', serviceId: serviceId ?? '' }));
				}
				return actions;
			})
		)
	);

	patientGraphGenerated$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientGraphActions.patientGraphGenerated),
			delay(TransitionDelayInMilliseconds),
			concatLatestFrom(() => this.store.select(entityIdentifiers)),
			filter(([action, identifiers]) => action.patientId === identifiers.patientId),
			map(([action]) =>
				patientGraphActions.patientGraphLoaded({
					patient: action.aggregates.find(agg => agg.aggregateType === PatientTypeName) as Patient,
					clients: action.aggregates.filter(agg => agg.aggregateType === ClientTypeName) as Client[],
					visits: action.aggregates.filter(agg => agg.aggregateType === VisitTypeName) as Visit[],
					attachments: action.aggregates.filter(agg => agg.aggregateType === AttachmentTypeName) as Attachment[],
					clinicalNotes: action.aggregates.filter(agg => agg.aggregateType === ClinicalNoteTypeName) as ClinicalNote[],
					tasks: action.aggregates.filter(agg => agg.aggregateType === TaskTypeName) as PatientRecordTask[],
					documentInstances: action.aggregates.filter(agg => agg.aggregateType === DocumentInstanceTypeName) as DocumentInstance[]
				})
			),
			catchError(error => of(clientErrored({ toastMessage: 'Patient graph generation failed', errorMessage: this.errorService.getErrorMessage(error) })))
		)
	);

	patientGraphChanged$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientGraphActions.patientGraphChanged),
			concatLatestFrom(() => this.store.select(patientId)),
			filter(([action, selectedPatientId]) => action.patientId === selectedPatientId),
			map(([action,]) => action.changes),
			concatMap(changes => {
				const actions: any[] = [];
				changes.forEach(change => {
					// guard against b/e not sending the operation
					if (!change.operation) throw new Error('Error processing patientGraphChanged action: operation is missing');

					// visits can be updated or added
					if (change.aggregate.aggregateType === VisitTypeName) {
						if (change.operation === GraphAggregateChangeOperation.Update) {
							actions.push(patientGraphActions.visitUpdated({ partialVisit: change.aggregate as Visit }));
						}
						else if (change.operation === GraphAggregateChangeOperation.Addition) {
							actions.push(patientGraphActions.visitAdded({ visit: change.aggregate as Visit }));
						}
					}
					// patients can be updated
					else if (change.aggregate.aggregateType === PatientTypeName && change.operation === GraphAggregateChangeOperation.Update) {
						actions.push(patientGraphActions.patientUpdated({ partialPatient: change.aggregate }));
					}
					// clients can be updated
					else if (change.aggregate.aggregateType === ClientTypeName && change.operation === GraphAggregateChangeOperation.Update) {
						actions.push(patientGraphActions.clientUpdated({ partialClient: change.aggregate }));
					}
					// attachments can be updated, added or removed
					else if (change.aggregate.aggregateType === AttachmentTypeName) {
						if (change.operation === GraphAggregateChangeOperation.Update) {
							actions.push(patientGraphActions.attachmentUpdated({ partialAttachment: change.aggregate as Attachment }));
						}
						else if (change.operation === GraphAggregateChangeOperation.Addition) {
							actions.push(patientGraphActions.attachmentAdded({ attachment: change.aggregate as Attachment }));
						}
						else if (change.operation === GraphAggregateChangeOperation.Removal) {
							actions.push(patientGraphActions.attachmentRemoved({ id: change.aggregate.id }));
						}
					}
					// clinical notes can be updated or added
					else if (change.aggregate.aggregateType === ClinicalNoteTypeName) {
						if (change.operation === GraphAggregateChangeOperation.Update) {
							actions.push(patientGraphActions.clinicalNoteUpdated({ partialClinicalNote: change.aggregate as ClinicalNote }));
						}
						else if (change.operation === GraphAggregateChangeOperation.Addition) {
							actions.push(patientGraphActions.clinicalNoteAdded({ clinicalNote: change.aggregate as ClinicalNote }));
						}
					}
					// tasks can be updated or added
					else if (change.aggregate.aggregateType === TaskTypeName) {
						if (change.operation === GraphAggregateChangeOperation.Update) {
							actions.push(patientGraphActions.taskUpdated({ partialTask: change.aggregate as PatientRecordTask }));
						}
						else if (change.operation === GraphAggregateChangeOperation.Addition) {
							actions.push(patientGraphActions.taskAdded({ task: change.aggregate as PatientRecordTask }));
						}
					}
					// document instances can be updated or added
					else if (change.aggregate.aggregateType === DocumentInstanceTypeName) {
						if (change.operation === GraphAggregateChangeOperation.Update) {
							actions.push(patientGraphActions.documentInstanceUpdated({ partialDocumentInstance: change.aggregate as DocumentInstance }));
						}
						else if (change.operation === GraphAggregateChangeOperation.Addition) {
							actions.push(patientGraphActions.documentInstanceAdded({ documentInstance: change.aggregate as DocumentInstance }));
						}
					}
				});
				return actions;
			}),
			catchError(error => of(clientErrored({ toastMessage: 'Patient graph change failed', errorMessage: this.errorService.getErrorMessage(error) })))
		)
	);

	patientGraphValueChanged$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientGraphActions.patientGraphValueChanged),
			concatLatestFrom(() => this.store.select(patientId)),
			filter(([action, selectedPatientId]) => action.patientId === selectedPatientId),
			map(([action]) => ({ operation: action.operation, valueTypeName: action.valueTypeName, value: action.value })),
			concatMap(change => {
				// communication records can be added, updated or removed
				if (change.valueTypeName === CommunicationRecordTypeName) {
					if (change.operation === GraphAggregateChangeOperation.Addition) {
						return of(patientGraphActions.communicationRecordAdded({ record: change.value as CommunicationRecord }));
					}
					else if (change.operation === GraphAggregateChangeOperation.Update) {
						return of(patientGraphActions.communicationRecordUpdated({ record: change.value as CommunicationRecord }));
					}
					else if (change.operation === GraphAggregateChangeOperation.Removal) {
						return of(patientGraphActions.communicationRecordRemoved({ recordId: change.value as number }));
					}
				}
				// clinical note emails can only be added
				if (change.valueTypeName === ClinicalNoteEmailTypeName && change.operation === GraphAggregateChangeOperation.Addition) {
					return of(patientGraphActions.clinicalNoteEmailAdded({ email: change.value as ClinicalNoteEmail }));
				}
				return of(clientErrored({ toastMessage: 'Patient graph value change failed', errorMessage: `Unexpected value type: ${change.valueTypeName}` }));
			}),
			catchError(error => of(clientErrored({ toastMessage: 'Patient graph value change failed', errorMessage: this.errorService.getErrorMessage(error) })))
		)
	);

	resubscribePatientInitiated$ = createEffect(() =>
		this.actions$.pipe(
			ofType(connectHubCompleted, patientGraphActions.patientGraphSystemRestarted),
			concatLatestFrom(() => this.store.select(entityIdentifiers)),
			filter(([, entityIdentifiers]) => !!entityIdentifiers.patientId),
			map(([, entityIdentifiers]) => patientGraphActions.subscribePatientRequested({ patientChanged: false, patientId: entityIdentifiers.patientId, taskId: entityIdentifiers.taskId, serviceId: entityIdentifiers.serviceId })),
		)
	);

	reloadPatientRequested$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientGraphActions.reloadPatientRequested),
			concatLatestFrom(() => [this.store.select(entityIdentifiers), this.store.select(sheetId)]),
			switchMap(([, entityIdentifiers, sheetId]) => {
				let actions: any[] = [patientGraphActions.unsubscribePatientRequested()];

				if (sheetId) {
					actions.push(sheetGraphActions.unsubscribeSheetRequested({ sheetId }));
					actions.push(sheetActions.sheetCleared());
				}

				actions.push(patientGraphActions.subscribePatientRequested({ patientChanged: true, patientId: entityIdentifiers.patientId, taskId: entityIdentifiers.taskId, serviceId: entityIdentifiers.serviceId }));

				return actions;
			}),
		)
	);
}